/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Row,
  Col,
  notification,
  DatePicker,
  Button,
  Radio,
  InputNumber,
  Upload,
  Spin,
  message,
  Checkbox,
} from 'antd';
import { Upload as UploadIcon, Download, FileText } from 'lucide-react';
import moment from 'moment';
import axiosConfig from '../../services/axiosConfig';
import axios from 'axios';
import PqtFormSelect from './PqtFormSelect';
import './ProjectHeatProfile.css';

const COATING_THICKNESS_UNIT_OPTIONS = [
  { value: 'µm', label: 'µm' },
  { value: 'mm', label: 'mm' },
  { value: 'inch', label: 'inch' },
];

/** Shorten long S3-style names for the file chip without losing extension. */
function displayFileName(name, maxLen = 52) {
  if (name == null || typeof name !== 'string') return '';
  if (name.length <= maxLen) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot) : '';
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const budget = maxLen - ext.length - 1;
  if (budget < 14) return `${name.slice(0, maxLen - 1)}…`;
  const head = Math.ceil(budget * 0.55);
  const tail = budget - head;
  return `${stem.slice(0, head)}…${stem.slice(-tail)}${ext}`;
}

const ProjectHeatProfile = (props) => {
  const { selectedProject, selectedSOW, editingRecord, onClose } = props;

  const user = JSON.parse(localStorage.getItem('user'));
  let name;
  if (user.name) {
    name = user.name;
  } else {
    name = user['custom:first_name'] + ' ' + user['custom:last_name'];
  }

  const validation_fields = {
    reportNumber: 'Report Number',
    projectName: 'Project Name',
    projectNumber: 'Project Number',
    trailDate: 'Trial Date',
    generatorId: 'Generator Id',
    inductionCoilId: 'Induction Coil ID',
    steelOD: 'Steel OD',
    wt: 'Wt',
    generatorSettings: 'Generator Settings',
    coilLength: 'CoilLength',
    cableLength: 'Cable length',
    numberOfTurns: 'Number of Turns',
    materialClass: 'Material Class',
    material: 'Material',
    cutbackLength: 'Cut back Length',
    parentCoatingThickness: 'Parent Coating Thickness',
    probeTemp: 'Probe Temp',
    probeTempLocation: 'Probe temp Location',
  };

  const initialFormState = {
    reportNumber: '',
    projectName: selectedProject?.projectName || '',
    projectNumber: selectedProject?.projectId || '',
    reportAuthor: name,
    trailDate: '',
    generatorId: '',
    inductionCoilId: '',
    steelOD: '',
    wt: '',
    generatorSettings: '',
    coilLength: '',
    cableLength: '',
    numberOfTurns: '',
    materialClass: '',
    material: '',
    stages: 1,
    maxTemp: '0',
    minTemp: '0',
    maxTempS1: '0',
    minTempS1: '0',
    cutbackLength: '',
    parentCoatingThickness: '',
    probeTemp: '',
    probeTempLocation: '',
    signatureRequired: 0,
    projectId: selectedProject?.id,
    sowId: selectedSOW?.id,
    deployEnv: process.env.REACT_APP_STAGE,
    imageFileDetails: {},
    required: {
      generatorSettings: 'Yes',
      coilLength: 'Yes',
      cableLength: 'Yes',
      numberOfTurns: 'Yes',
    },
    units: {
      steelOD: 'mm',
      wt: 'mm',
      coilLength: 'mm',
      cableLength: 'meters',
      parentCoatingThickness: 'mm',
    },
  };

  const materialClassOptions = [
    'FBE',
    'Liquid Epoxy',
    'Single Layer FBE',
    'Dual Layer FBE',
    'Others',
  ];

  const materialOptions = {
    FBE: [
      'Pipeclad 2000 Slow Gel',
      'Pipeclad 2000 LAT',
      'Pipeclad Hot 150',
      'Pipeclad Hot 150 Flex',
      'Pipeclad Hot 12',
      'Pipeclad Hot 120 Flex',
      'Axalta Nap-Gard',
      'Jotapipe AC 1003 XT',
    ],
    'Liquid Epoxy': ['Canusa Liquid Epoxy Type P'],
    'Single Layer FBE': ['Axalta Nap-Gard 72555LG'],
    'Dual Layer FBE': ['Axalta Nap-Gard 7-2675'],
  };

  const predefinedTemperatureRanges = {
    'Pipeclad 2000 Slow Gel': { min: 205, max: 240 },
    'Pipeclad 2000 LAT': { min: 175, max: 210 },
    'Pipeclad Hot 150': { min: 204, max: 240 },
    'Pipeclad Hot 150 Flex': { min: 204, max: 240 },
    'Pipeclad Hot 12': { min: 205, max: 240 },
    'Pipeclad Hot 120 Flex': { min: 205, max: 240 },
    'Canusa Liquid Epoxy Type P': { min: 175, max: 210, minS1: 30, maxS1: 60 },
    'Axalta Nap-Gard': { min: 218, max: 239, minS1: 0, maxS1: 180 },
    'Axalta Nap-Gard 72555LG': { min: 225, max: 239 },
    'Axalta Nap-Gard 7-2675': { min: 218, max: 239 },
    'Jotapipe AC 1003 XT': { min: 232, max: 250 },
  };

  // State variables
  const [formData, setFormData] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFile, setOutputFile] = useState('');
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [isOtherMaterialClass, setIsOtherMaterialClass] = useState(false);
  const [pdfReportDetails, setPDFReportDetails] = useState([]);
  const [textFileDetails, setTextFileDetails] = useState([]);
  const [isGenerated, setIsGenerated] = useState(false);

  // Units states
  const [steelODUnit, setSteelODUnit] = useState('mm');
  const [wtUnits, setWtUnits] = useState('mm');
  const [coilLengthUnit, setCoilLengthUnit] = useState('mm');
  const [cableUnit, setcableUnit] = useState('meters');
  const [parentCoatingThicknessUnit, setParentCoatingThicknessUnit] = useState('mm');

  // Add temperature unit constant
  const temperatureUnit = '°C';

  const materialClassPqtOptions = useMemo(
    () => materialClassOptions.map((o) => ({ value: o, label: o })),
    []
  );

  const materialPqtOptions = useMemo(() => {
    const list = materialOptions[formData.materialClass];
    return Array.isArray(list) ? list.map((o) => ({ value: o, label: o })) : [];
  }, [formData.materialClass]);

  useEffect(() => {
    // Initialize form with project data
    if (selectedProject) {
      console.log('Selected Project Structure:', selectedProject); // Debug log
      console.log('Project Properties:', {
        id: selectedProject.id,
        projectName: selectedProject.projectName,
        projectId: selectedProject.projectId,
        name: selectedProject.name,
        projectNumber: selectedProject.projectNumber,
      }); // Debug log
      const initialData = {
        ...initialFormState,
        projectName: selectedProject.projectName,
        projectNumber: selectedProject.projectId,
        projectId: selectedProject.id,
        sowId: selectedSOW?.id,
      };
      console.log('Initial Data:', initialData); // Debug log
      setFormData(initialData);
      form.setFieldsValue(initialData);
    }
  }, [selectedProject, selectedSOW, form]);

  // Additional useEffect to ensure project fields are always set
  useEffect(() => {
    if (selectedProject && (!formData.projectName || !formData.projectNumber)) {
      console.log('Force updating project fields:', selectedProject);
      const projectName = selectedProject.projectName || '';
      const projectNumber = selectedProject.projectId || '';

      if (projectName || projectNumber) {
        setFormData((prevData) => ({
          ...prevData,
          projectName: projectName,
          projectNumber: projectNumber,
        }));

        form.setFieldsValue({
          projectName: projectName,
          projectNumber: projectNumber,
        });

        console.log('Updated project fields:', { projectName, projectNumber });
      }
    }
  }, [selectedProject, formData.projectName, formData.projectNumber, form]);

  // Handle editing mode - populate form with existing record data
  useEffect(() => {
    if (editingRecord) {
      console.log('ProjectHeatProfile - editing mode with record:', editingRecord);

      // Prepare the data for form, handling date formatting and boolean/radio conversions
      const editData = {
        ...editingRecord,
        trailDate: editingRecord.trailDate ? moment(editingRecord.trailDate) : null,
        // Convert boolean signatureRequired to radio button value (0/1)
        signatureRequired: editingRecord.signatureRequired === true ? 1 : 0,
        // Ensure stages is a number
        stages: parseInt(editingRecord.stages) || 1,
        // Initialize required fields based on existing data
        required: {
          generatorSettings: editingRecord.generatorSettings ? 'Yes' : 'No',
          coilLength: editingRecord.coilLength ? 'Yes' : 'No',
          cableLength: editingRecord.cableLength ? 'Yes' : 'No',
          numberOfTurns: editingRecord.numberOfTurns ? 'Yes' : 'No',
          cutbackLength: editingRecord.cutbackLength ? 'Yes' : 'No',
          parentCoatingThickness: editingRecord.parentCoatingThickness ? 'Yes' : 'No',
          probeTemp: editingRecord.probeTemp ? 'Yes' : 'No',
          probeTempLocation: editingRecord.probeTempLocation ? 'Yes' : 'No',
        },
        // Initialize units (assuming they are stored as separate fields or default to 'mm')
        units: editingRecord.units || {
          steelOD: 'mm',
          wt: 'mm',
          coilLength: 'mm',
          cableLength: 'meters',
          parentCoatingThickness: 'mm',
        },
        // Ensure project data is preserved from props
        projectName:
          selectedProject?.projectName ||
          editingRecord.projectName ||
          editingRecord.project?.projectName,
        projectNumber:
          selectedProject?.projectId ||
          editingRecord.projectNumber ||
          editingRecord.project?.projectId,
        projectId: selectedProject?.id || editingRecord.projectId,
        sowId: selectedSOW?.id || editingRecord.sowId,
      };

      console.log('Setting form with edit data:', editData);
      setFormData(editData);
      form.setFieldsValue(editData);

      // Set unit states if they exist in the editing record
      if (editingRecord.units) {
        setSteelODUnit(editingRecord.units.steelOD || 'mm');
        setWtUnits(editingRecord.units.wt || 'mm');
        setCoilLengthUnit(editingRecord.units.coilLength || 'mm');
        setcableUnit(editingRecord.units.cableLength || 'meters');
        setParentCoatingThicknessUnit(editingRecord.units.parentCoatingThickness || 'mm');
      }

      // Set existing file details for preview
      if (editingRecord.textFileDetails && editingRecord.textFileDetails.s3Url) {
        console.log('Setting existing text file for preview:', editingRecord.textFileDetails);
        // Create a mock file object for preview purposes
        const existingFile = {
          name: editingRecord.textFileDetails.fileName || 'Existing File',
          uid: 'existing-file',
          status: 'done',
          url: editingRecord.textFileDetails.s3Url,
          isExisting: true, // Flag to identify it's an existing file
        };
        setSelectedFile(existingFile);
        console.log('Set existing file object:', existingFile);
      }

      // Set existing PDF file if available
      if (editingRecord.pdfReportDetails && editingRecord.pdfReportDetails.s3Url) {
        console.log('Setting existing PDF for preview:', editingRecord.pdfReportDetails);
        setOutputFile(editingRecord.pdfReportDetails.s3Url);
      }
    }
  }, [editingRecord, form, selectedProject, selectedSOW]);

  // Set temperature based on material
  const setTemperatureBasedOnMaterial = (material) => {
    if (predefinedTemperatureRanges[material]) {
      setFormData((prevData) => ({
        ...prevData,
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
        stages: 1,
      }));
      form.setFieldsValue({
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
      });
    }

    if (
      predefinedTemperatureRanges[material] &&
      (material == 'Canusa Liquid Epoxy Type P' || material == 'Axalta Nap-Gard')
    ) {
      setFormData((prevData) => ({
        ...prevData,
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
        minTempS1: predefinedTemperatureRanges[material].minS1,
        maxTempS1: predefinedTemperatureRanges[material].maxS1,
        stages: '2',
      }));
      form.setFieldsValue({
        minTemp: predefinedTemperatureRanges[material].min,
        maxTemp: predefinedTemperatureRanges[material].max,
        minTempS1: predefinedTemperatureRanges[material].minS1,
        maxTempS1: predefinedTemperatureRanges[material].maxS1,
        stages: '2',
      });
    }
  };

  // Handle input change
  const handleInputChange = (name, value) => {
    if (name === 'trailDate') {
      value = moment(value).format('YYYY-MM-DD');
    }

    if (name === 'materialClass') {
      setFormData((prevData) => ({ ...prevData, customMaterialClass: '' }));

      setFormData((prevData) => ({
        ...prevData,
        material: '',
        maxTemp: '0',
        minTemp: '0',
        minTempS1: '0',
        maxTempS1: '0',
        stages: '1',
      }));
      form.setFieldsValue({
        material: '',
        maxTemp: '',
        minTemp: '',
        minTempS1: '',
        maxTempS1: '',
        stages: 1,
      });
    }

    if (name === 'materialClass' && value === 'Others') {
      setFormData((prevData) => ({ ...prevData, material: '' }));
      form.setFieldsValue({
        minTemp: '',
        maxTemp: '',
        minTempS1: '',
        maxTempS1: '',
        stages: 1,
      });
    }

    if (name === 'material') {
      setTemperatureBasedOnMaterial(value);
    }

    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle file change
  const handleChange = async (info) => {
    const invalidChars = /[<>:"/\\|?*']/;
    const { status, name, originFileObj } = info.file;

    if (status) {
      if (invalidChars.test(name)) {
        notification.warning({
          message: 'Invalid File Name',
          description: 'The file name contains invalid characters: < > : " / \\ | ? * \'',
        });
        // Optionally, you can clear the file list if an invalid file is selected
        setFileList([]);
      } else {
        setSelectedFile(originFileObj);
        setFileList([info.file]);
      }
    } else if (status === 'error') {
      console.error(`${name} file upload failed.`);
    }
  };

  // Handle reset
  const handleReset = () => {
    setFormData(initialFormState);
    setSelectedFile(null);
    setFileList([]);
    setOutputFile('');
    setSteelODUnit('mm');
    setWtUnits('mm');
    setCoilLengthUnit('mm');
    setcableUnit('meters');
    setParentCoatingThicknessUnit('mm');
    form.resetFields();
  };

  // Helper function to append units to specific keys
  const appendUnit = (key, value) => `${value} ${formData.units[key]}`;

  // Upload file to S3 function
  const uploadFileToS3 = async (selectedFile) => {
    try {
      const textFileTos3Response = await axiosConfig.get(
        `/project-heat-profile/signed-url?fileName=${selectedFile.name}&deployEnv=${process.env.REACT_APP_STAGE}`
      );
      if (textFileTos3Response.status === 200) {
        const textFileTos3 = textFileTos3Response.data;
        const uploadPresignedUrlResponse = await axios.put(
          textFileTos3.data.preSignedUrl,
          selectedFile,
          {
            headers: {
              'Content-Disposition': 'attachment',
              'Content-Type': 'text/plain',
            },
          }
        );

        if (uploadPresignedUrlResponse.status === 200) {
          const fileInfo = {
            s3Url: textFileTos3.data.s3Url,
            fileName: textFileTos3.data.fileName,
            s3Key: textFileTos3.data.s3Key,
          };

          setTextFileDetails(fileInfo);
          return fileInfo;
        } else {
          console.error('Error uploading file to presigned URL:', uploadPresignedUrlResponse);
        }
      } else {
        console.error('Error getting signed URL:', textFileTos3Response);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Generate PDF
  const generatePdf = async () => {
    // If project fields are missing, try to populate them one more time
    if (selectedProject && (!formData.projectName || !formData.projectNumber)) {
      const projectName = selectedProject.projectName || 'Unknown Project';
      const projectNumber = selectedProject.projectId || 'Unknown Number';

      setFormData((prevData) => ({
        ...prevData,
        projectName: projectName,
        projectNumber: projectNumber,
      }));

      form.setFieldsValue({
        projectName: projectName,
        projectNumber: projectNumber,
      });
    }

    // Validation logic
    const missingFields = Object.keys(formData).filter((key) => {
      if (
        (key === 'material' || key === 'customMaterialClass') &&
        formData.materialClass != 'Liquid Epoxy' &&
        formData.materialClass != 'FBE'
      ) {
        return false;
      }
      return formData[key] === '';
    });

    if (missingFields.includes('material') && formData.material === '' && isOtherMaterialClass)
      missingFields.splice(missingFields.indexOf('material'), 1);

    if (missingFields.includes('customMaterialClass')) {
      missingFields.splice(missingFields.indexOf('customMaterialClass'), 1);
    }

    if (missingFields.includes('approvedBySign')) {
      missingFields.splice(missingFields.indexOf('approvedBySign'), 1);
    }
    if (missingFields.includes('approved2BySign')) {
      missingFields.splice(missingFields.indexOf('approved2BySign'), 1);
    }
    if (missingFields.includes('preparedBySign')) {
      missingFields.splice(missingFields.indexOf('preparedBySign'), 1);
    }
    if (missingFields.includes('generatorSettings')) {
      if (formData.required.generatorSettings === 'No') {
        missingFields.splice(missingFields.indexOf('generatorSettings'), 1);
      }
    }

    if (missingFields.includes('coilLength')) {
      if (formData.required.coilLength === 'No') {
        missingFields.splice(missingFields.indexOf('coilLength'), 1);
      }
    }

    if (missingFields.includes('cableLength')) {
      if (formData.required.cableLength === 'No') {
        missingFields.splice(missingFields.indexOf('cableLength'), 1);
      }
    }

    if (missingFields.includes('numberOfTurns')) {
      if (formData.required.numberOfTurns === 'No') {
        missingFields.splice(missingFields.indexOf('numberOfTurns'), 1);
      }
    }

    if (missingFields.length > 10) {
      const missingFieldNames = missingFields.map((field) => validation_fields[field]);
      console.log(missingFields, missingFieldNames);
      message.error(`Please fill in the following fields: ${missingFieldNames.join(', ')}`);
      return;
    } else if (missingFields.length > 0) {
      message.error(`Please fill the required fields`);
      console.log(missingFields);
      return;
    }

    if (formData.minTemp > formData.maxTemp) {
      message.error(`MinTemp is must be lesser or equal to ${formData.maxTemp}`);
      return;
    }

    if (formData.minTempS1 > formData.maxTempS1) {
      message.error(`MinTempS1 is must be lesser or equal to ${formData.maxTempS1}`);
      return;
    }

    setLoading(true);

    if (selectedFile) {
      const updatedFormData = new FormData();
      let heatprofiledata = {};

      // Helper function to append units to specific keys
      const appendUnit = (key, value) => `${value} ${formData.units[key]}`;

      // Iterate over formData entries
      Object.entries(formData).forEach(([key, value]) => {
        // Handle specific keys requiring unit appending
        const unitKeys = ['steelOD', 'coilLength', 'wt', 'cableLength', 'parentCoatingThickness'];
        if (unitKeys.includes(key)) {
          value = appendUnit(key, value);
        }

        if (key == 'signatureRequired' && value == false) {
          value = 'False';
        }

        heatprofiledata[key] = value;
        if (key == 'required') {
          value = JSON.stringify(value);
        }
        if (key == 'units') {
          value = JSON.stringify(value);
        }
        updatedFormData.append(key, value);
      });

      // Add missing fields that are required by the API
      updatedFormData.append('imageFileDetails', JSON.stringify(formData.imageFileDetails || {}));
      updatedFormData.append('textFileDetails', JSON.stringify(formData.textFileDetails || {}));
      updatedFormData.append('pdfReportDetails', JSON.stringify(formData.pdfReportDetails || {}));

      // Add ID if editing
      if (editingRecord && editingRecord.id) {
        updatedFormData.append('id', editingRecord.id);
      }

      // Add timestamps
      updatedFormData.append('createdAt', formData.createdAt || moment().format('YYYY-MM-DD'));
      updatedFormData.append('updatedAt', moment().format('YYYY-MM-DD'));
      updatedFormData.append('totalCount', formData.totalCount || '0');

      updatedFormData.append('file', selectedFile);
      updatedFormData.append('GeneratedDate', moment().utc().format('DD-MMM-YYYY HH:mm'));
      formData.units['GeneratedDate'] = moment().utc().format('DD-MMM-YYYY HH:mm');

      try {
        const txtFileInfo = await uploadFileToS3(selectedFile);

        const response = await axios.post(
          process.env.REACT_APP_AWS_HEAT_PROFILE_REPORT_GENERATOR,
          updatedFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        const reportUrlObject = response.data.report_url;

        setPDFReportDetails(reportUrlObject.Report_s3Url);
        setOutputFile(reportUrlObject.Report_s3Url.s3Url);
        setTextFileDetails(txtFileInfo);

        // Prepare the heat profile data in the expected format
        const heatProfileData = {
          projectId: selectedProject?.id || 2,
          reportAuthor: formData.reportAuthor || name || 'Project User',
          trailDate: formData.trailDate
            ? typeof formData.trailDate === 'string'
              ? formData.trailDate
              : moment(formData.trailDate).format('YYYY-MM-DD')
            : null,
          generatorId: formData.generatorId,
          inductionCoilId: formData.inductionCoilId,
          steelOD: parseFloat(formData.steelOD) || 0,
          wt: parseFloat(formData.wt) || 0,
          generatorSettings: formData.generatorSettings,
          coilLength: parseFloat(formData.coilLength) || 0,
          cableLength: parseFloat(formData.cableLength) || 0,
          numberOfTurns: parseInt(formData.numberOfTurns) || 0,
          materialClass: formData.materialClass,
          material: formData.material,
          stages: parseInt(formData.stages) || 0,
          maxTemp: parseFloat(formData.maxTemp) || 0,
          minTemp: parseFloat(formData.minTemp) || 0,
          maxTempS1: parseFloat(formData.maxTempS1) || 0,
          minTempS1: parseFloat(formData.minTempS1) || 0,
          cutbackLength: formData.cutbackLength,
          parentCoatingThickness: parseFloat(formData.parentCoatingThickness) || 0,
          probeTemp: parseFloat(formData.probeTemp) || 0,
          probeTempLocation: formData.probeTempLocation,
          signatureRequired: Boolean(formData.signatureRequired),
          reportNumber: formData.reportNumber,
          pdfReportDetails: reportUrlObject.Report_s3Url,
          textFileDetails: txtFileInfo,
          imageFileDetails: reportUrlObject.Image_s3Url,
        };

        // Send the exact JSON structure as required by the API
        const requestBody = {
          heatProfile: heatProfileData,
        };

        console.log('Sending request body:', JSON.stringify(requestBody, null, 2));

        // Determine if this is create or update operation
        if (editingRecord && editingRecord.id) {
          // Update operation - use updateListData
          console.log('Updating existing heat profile with ID:', editingRecord.id);
          await updateListData(reportUrlObject, txtFileInfo);
        } else {
          // Create operation - use handleSubmit
          console.log('Creating new heat profile');
          await handleSubmit(reportUrlObject, txtFileInfo);
        }
      } catch (error) {
        console.error('Error:', error);
        message.error(
          `${selectedFile.name} file upload failed. Please select a valid file to upload`
        );
      }
    } else {
      message.error('Please select a valid file');
    }
    setLoading(false);
  };

  // Handle submit for new records
  const handleSubmit = async (reportUrlObject, txtFileInfo) => {
    setLoading(true);
    const payload = {
      heatProfile: {
        projectId: selectedProject?.id || 2,
        reportAuthor: formData.reportAuthor || name || 'Project User',
        trailDate: formData.trailDate
          ? typeof formData.trailDate === 'string'
            ? formData.trailDate
            : moment(formData.trailDate).format('YYYY-MM-DD')
          : null,
        generatorId: formData.generatorId,
        inductionCoilId: formData.inductionCoilId,
        steelOD: parseFloat(formData.steelOD) || 0,
        wt: parseFloat(formData.wt) || 0,
        generatorSettings: formData.generatorSettings,
        coilLength: parseFloat(formData.coilLength) || 0,
        cableLength: parseFloat(formData.cableLength) || 0,
        numberOfTurns: parseInt(formData.numberOfTurns) || 0,
        materialClass: formData.materialClass,
        material: formData.material,
        stages: parseInt(formData.stages) || 0,
        maxTemp: parseFloat(formData.maxTemp) || 0,
        minTemp: parseFloat(formData.minTemp) || 0,
        maxTempS1: parseFloat(formData.maxTempS1) || 0,
        minTempS1: parseFloat(formData.minTempS1) || 0,
        cutbackLength: formData.cutbackLength,
        parentCoatingThickness: parseFloat(formData.parentCoatingThickness) || 0,
        probeTemp: parseFloat(formData.probeTemp) || 0,
        probeTempLocation: formData.probeTempLocation,
        signatureRequired: Boolean(formData.signatureRequired),
        reportNumber: formData.reportNumber,
        pdfReportDetails: reportUrlObject.Report_s3Url,
        textFileDetails: txtFileInfo,
        imageFileDetails: reportUrlObject.Image_s3Url,
      },
    };

    try {
      const response = await axiosConfig.post('/project-heat-profile', payload);
      message.success(`Project Heat Profile created successfully`);
      onClose();

      if (formData['signatureRequired'] && formData['signatureRequired'] == true) {
        setIsGenerated(true);
      }
    } catch (error) {
      console.error('Error submitting data:', error);
      message.error(`Project Heat Profile creation failed.`);
    }
    setLoading(false);
  };

  // Update list data for editing records
  const updateListData = async (reportUrlObject, txtFileInfo) => {
    setLoading(true);
    const payload = {
      heatProfile: {
        projectId: selectedProject?.id || 2,
        reportAuthor: formData.reportAuthor || name || 'Project User',
        trailDate: formData.trailDate
          ? typeof formData.trailDate === 'string'
            ? formData.trailDate
            : moment(formData.trailDate).format('YYYY-MM-DD')
          : null,
        generatorId: formData.generatorId,
        inductionCoilId: formData.inductionCoilId,
        steelOD: parseFloat(formData.steelOD) || 0,
        wt: parseFloat(formData.wt) || 0,
        generatorSettings: formData.generatorSettings,
        coilLength: parseFloat(formData.coilLength) || 0,
        cableLength: parseFloat(formData.cableLength) || 0,
        numberOfTurns: parseInt(formData.numberOfTurns) || 0,
        materialClass: formData.materialClass,
        material: formData.material,
        stages: parseInt(formData.stages) || 0,
        maxTemp: parseFloat(formData.maxTemp) || 0,
        minTemp: parseFloat(formData.minTemp) || 0,
        maxTempS1: parseFloat(formData.maxTempS1) || 0,
        minTempS1: parseFloat(formData.minTempS1) || 0,
        cutbackLength: formData.cutbackLength,
        parentCoatingThickness: parseFloat(formData.parentCoatingThickness) || 0,
        probeTemp: parseFloat(formData.probeTemp) || 0,
        probeTempLocation: formData.probeTempLocation,
        signatureRequired: Boolean(formData.signatureRequired),
        reportNumber: formData.reportNumber,
        pdfReportDetails: reportUrlObject.Report_s3Url,
        textFileDetails: txtFileInfo,
        imageFileDetails: reportUrlObject.Image_s3Url,
      },
    };

    try {
      const response = await axiosConfig.put(`/project-heat-profile/${editingRecord.id}`, payload);
      message.success(`Project Heat Profile updated successfully`);
      onClose();

      if (formData['signatureRequired'] && formData['signatureRequired'] == true) {
        setIsGenerated(true);
      }
    } catch (error) {
      console.error('Error updating data:', error);
      message.error(`Project Heat Profile update failed.`);
    }
    setLoading(false);
  };

  // Handle units change
  const handleUnitsChange = (type, value) => {
    const category = 'units';
    setFormData((prevOptions) => {
      const updatedCategory = { ...prevOptions[category] };
      updatedCategory[type] = value;

      return {
        ...prevOptions,
        [category]: updatedCategory,
      };
    });
  };

  // Get category value
  const getCategoryValue1 = (type) => {
    const category = 'units';
    if (formData.hasOwnProperty(category)) {
      return formData[category][type];
    } else {
      return 'mm';
    }
  };

  // Check if array is not empty (for required fields)
  const isArrayNotEmpty = (key) => {
    if (formData.required && formData.required[key]) {
      return formData.required[key] === 'Yes';
    }
    return false;
  };

  // Handle header checkbox change
  const handleHeaderCheckboxChange = (key, checked) => {
    const category = 'required';
    setFormData((prevOptions) => {
      const updatedCategory = { ...prevOptions[category] };
      updatedCategory[key] = checked ? 'Yes' : 'No';

      return {
        ...prevOptions,
        [category]: updatedCategory,
      };
    });
  };

  // Check if any property has a value
  const isAnyPropertyWithValue = () => {
    return Object.values(formData.required || {}).some((value) => value === 'Yes');
  };

  // Update required check for all fields
  const UpdateRequiredCheck = (checked) => {
    const category = 'required';
    setFormData((prevOptions) => {
      const updatedCategory = { ...prevOptions[category] };

      // Update all required fields based on the master checkbox
      Object.keys(updatedCategory).forEach((key) => {
        updatedCategory[key] = checked ? 'Yes' : 'No';
      });

      return {
        ...prevOptions,
        [category]: updatedCategory,
      };
    });
  };

  // Disabled date function
  const disabledDate = (current) => {
    return current && current > moment().endOf('day');
  };

  return (
    <Modal
      width={Math.min(1040, typeof window !== 'undefined' ? window.innerWidth - 32 : 1040)}
      className="pipeline-modal heatprofile-modal heat-profile-modal pqt-project-hp-modal-shell"
      title={editingRecord ? 'Edit Heat Profile' : 'Add New Heat Profile'}
      centered
      visible={true}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => generatePdf()}>
          {editingRecord ? 'Update' : 'Create'}
        </Button>,
      ]}
      maskClosable={false}
    >
      <Spin
        spinning={loading}
        tip={
          editingRecord
            ? 'Wait: Updating Project Heat Profile...'
            : 'Wait: Creating Project Heat Profile...'
        }
      >
        <Form name="basic" layout="vertical" autoComplete="off" form={form}>
          <div className="pqt-project-hp-form">
          <Row gutter={[20, 0]} className="upload-row pqt-project-hp-form__row">
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Report Number" name="reportNumber" required>
                <Input
                  value={formData.reportNumber}
                  onChange={(e) => handleInputChange('reportNumber', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Project Name" name="projectName" required>
                <Input
                  value={formData.projectName}
                  onChange={(e) => handleInputChange('projectName', e.target.value)}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Project Number" name="projectNumber" required>
                <Input
                  value={formData.projectNumber}
                  onChange={(e) => handleInputChange('projectNumber', e.target.value)}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Report Author" name="reportAuthor" required>
                <Input
                  value={formData.reportAuthor}
                  onChange={(e) => handleInputChange('reportAuthor', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Trial Date" name="trailDate" required>
                <DatePicker
                  className="pqt-hp-datepicker"
                  value={formData.trailDate ? moment(formData.trailDate) : null}
                  onChange={(date, dateString) => handleInputChange('trailDate', dateString)}
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Generator ID" name="generatorId" required>
                <Input
                  value={formData.generatorId}
                  onChange={(e) => handleInputChange('generatorId', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Induction Coil ID" name="inductionCoilId" required>
                <Input
                  value={formData.inductionCoilId}
                  onChange={(e) => handleInputChange('inductionCoilId', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Steel OD" name="steelOD" required>
                <div className="pqt-hp-num-unit">
                  <Input
                    value={formData.steelOD}
                    onChange={(e) => handleInputChange('steelOD', e.target.value)}
                    type="number"
                    inputMode="decimal"
                  />
                  <Radio.Group
                    className="pqt-hp-unit-radios"
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                    value={getCategoryValue1('steelOD')}
                    onChange={(e) => {
                      setSteelODUnit(e.target.value);
                      handleUnitsChange('steelOD', e.target.value);
                    }}
                  >
                    <Radio.Button value="mm">mm</Radio.Button>
                    <Radio.Button value="inch">in</Radio.Button>
                  </Radio.Group>
                </div>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Wt" name="wt" required>
                <div className="pqt-hp-num-unit">
                  <Input
                    value={formData.wt}
                    onChange={(e) => handleInputChange('wt', e.target.value)}
                    type="number"
                    inputMode="decimal"
                  />
                  <Radio.Group
                    className="pqt-hp-unit-radios"
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                    value={getCategoryValue1('wt')}
                    onChange={(e) => {
                      setWtUnits(e.target.value);
                      handleUnitsChange('wt', e.target.value);
                    }}
                  >
                    <Radio.Button value="mm">mm</Radio.Button>
                    <Radio.Button value="inch">in</Radio.Button>
                  </Radio.Group>
                </div>
              </Form.Item>
            </Col>

            {/* Material Class and Material */}
            <Col xs={24} md={12} xl={8}>
              <Form.Item label="Material Class" name="materialClass" required>
                <PqtFormSelect
                  placeholder="Select material class"
                  value={formData.materialClass}
                  options={materialClassPqtOptions}
                  onChange={(value) => {
                    handleInputChange('materialClass', value);
                    setIsOtherMaterialClass(value === 'Others');
                  }}
                />
              </Form.Item>
            </Col>
            {isOtherMaterialClass && (
              <Col xs={24} md={12} xl={8}>
                <Form.Item label="Other Material Class" name="customMaterialClass" required>
                  <Input
                    value={formData.materialClass}
                    onChange={(e) => handleInputChange('materialClass', e.target.value)}
                  />
                </Form.Item>
              </Col>
            )}
            {!isOtherMaterialClass && (
              <Col xs={24} md={12} xl={10}>
                <Form.Item label="Material" name="material" required>
                  <PqtFormSelect
                    placeholder="Select material"
                    value={formData.material}
                    options={materialPqtOptions}
                    disabled={isOtherMaterialClass || !formData.materialClass}
                    onChange={(value) => handleInputChange('material', value)}
                  />
                </Form.Item>
              </Col>
            )}

            {/* Temperature fields right after Material - Stage 1 */}
            {formData.stages == 1 && !isOtherMaterialClass && (
              <>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                    <Input value={formData.minTemp} disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Max Temp`} name="maxTemp" required>
                    <InputNumber
                      value={formData.maxTemp}
                      onChange={(value) => handleInputChange('maxTemp', value)}
                      min={parseFloat(formData.minTemp)}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Temperature fields for other material class - Stage 1 */}
            {formData.stages == 1 && isOtherMaterialClass && (
              <>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                    <Input
                      value={formData.minTemp}
                      onChange={(e) => handleInputChange('minTemp', e.target.value)}
                      type="number"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Max Temp`} name="maxTemp" required>
                    <InputNumber
                      value={formData.maxTemp}
                      onChange={(value) => handleInputChange('maxTemp', value)}
                      min={parseFloat(formData.minTemp)}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Two-stage temperature fields for specific materials */}
            {formData.stages == 2 && formData.material === 'Axalta Nap-Gard' && (
              <>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                    <Input value={formData.minTemp} disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Max Temp ${temperatureUnit}`} name="maxTemp" required>
                    <Input value={formData.maxTemp} disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Min Temp S1 `} name="minTempS1" required>
                    <Input value={formData.minTempS1} disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Max Temp S1`} name="maxTempS1" required>
                    <Input value={formData.maxTempS1} disabled />
                  </Form.Item>
                </Col>
              </>
            )}

            {formData.stages == 2 && formData.material == 'Canusa Liquid Epoxy Type P' && (
              <>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                    <Input value={formData.minTemp} disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Max Temp ${temperatureUnit}`} name="maxTemp" required>
                    <Input value={formData.maxTemp} disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Min Temp S1 `} name="minTempS1" required>
                    <Input value={formData.minTempS1} disabled />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} lg={3}>
                  <Form.Item label={`Max Temp S1`} name="maxTempS1" required>
                    <Input value={formData.maxTempS1} disabled />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Two-stage temperature fields for other material class */}
            {formData.stages == '2' &&
              formData.material !== 'Canusa Liquid Epoxy Type P' &&
              formData.material !== 'Axalta Nap-Gard' && (
                <>
                  <Col xs={12} sm={8} lg={3}>
                    <Form.Item label={`Min Temp ${temperatureUnit}`} name="minTemp" required>
                      <Input
                        value={formData.minTemp}
                        onChange={(e) => handleInputChange('minTemp', e.target.value)}
                        type="number"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={8} lg={3}>
                    <Form.Item label={`Max Temp`} name="maxTemp" required>
                      <InputNumber
                        value={formData.maxTemp}
                        onChange={(value) => handleInputChange('maxTemp', value)}
                        min={parseFloat(formData.minTemp)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={8} lg={3}>
                    <Form.Item label={`Min Temp S1 `} name="minTempS1" required>
                      <Input
                        value={formData.minTempS1}
                        onChange={(e) => handleInputChange('minTempS1', e.target.value)}
                        type="number"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={8} lg={3}>
                    <Form.Item label={`Max Temp S1`} name="maxTempS1" required>
                      <InputNumber
                        value={formData.maxTempS1}
                        onChange={(value) => handleInputChange('maxTempS1', value)}
                        min={parseFloat(formData.minTempS1)}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

            {isOtherMaterialClass && (
              <Col xs={24} md={8}>
                <Form.Item label="Stages" name="stages" required>
                  <PqtFormSelect
                    placeholder="Stages"
                    value={Number(formData.stages) || 1}
                    options={[
                      { value: 1, label: 'Stage one' },
                      { value: 2, label: 'Stage two' },
                    ]}
                    onChange={(value) => handleInputChange('stages', value)}
                  />
                </Form.Item>
              </Col>
            )}

            {/* Required to Report Section */}
            <Col span={24}>
              <hr className="pqt-hp-section-divider" />
            </Col>
            <Col span={24}>
              <div className="pqt-hp-section-title">
                <Checkbox
                  onChange={(e) => {
                    UpdateRequiredCheck(e.target.checked);
                  }}
                  checked={isAnyPropertyWithValue()}
                >
                  Required to report
                </Checkbox>
              </div>
            </Col>

            <Col span={24}>
              <div className="pqt-hp-report-grid">
                <Form.Item name="generatorSettings" className="pqt-hp-report-field-wrap">
                  <div className="pqt-hp-report-field">
                    <div className="pqt-hp-report-field__head">
                      <Checkbox
                        onChange={(e) => {
                          handleHeaderCheckboxChange('generatorSettings', e.target.checked);
                        }}
                        checked={isArrayNotEmpty('generatorSettings')}
                      />
                      <span className="pqt-hp-report-field__label">Generator settings</span>
                      {isArrayNotEmpty('generatorSettings') ? (
                        <span className="pqt-hp-report-field__req" aria-hidden>
                          *
                        </span>
                      ) : null}
                    </div>
                    <Input
                      disabled={!isArrayNotEmpty('generatorSettings')}
                      value={formData.generatorSettings}
                      onChange={(e) => handleInputChange('generatorSettings', e.target.value)}
                      maxLength={255}
                      placeholder="Value"
                    />
                  </div>
                </Form.Item>

                <Form.Item name="coilLength" className="pqt-hp-report-field-wrap">
                  <div className="pqt-hp-report-field">
                    <div className="pqt-hp-report-field__head">
                      <Checkbox
                        onChange={(e) => {
                          handleHeaderCheckboxChange('coilLength', e.target.checked);
                        }}
                        checked={isArrayNotEmpty('coilLength')}
                      />
                      <span className="pqt-hp-report-field__label">Coil length</span>
                      {isArrayNotEmpty('coilLength') ? (
                        <span className="pqt-hp-report-field__req" aria-hidden>
                          *
                        </span>
                      ) : null}
                    </div>
                    <div className="pqt-hp-num-unit">
                      <Input
                        disabled={!isArrayNotEmpty('coilLength')}
                        value={formData.coilLength}
                        onChange={(e) => handleInputChange('coilLength', e.target.value)}
                        type="number"
                        inputMode="decimal"
                        placeholder="Length"
                      />
                      <Radio.Group
                        className="pqt-hp-unit-radios"
                        optionType="button"
                        buttonStyle="solid"
                        size="small"
                        disabled={!isArrayNotEmpty('coilLength')}
                        value={getCategoryValue1('coilLength')}
                        onChange={(e) => {
                          setCoilLengthUnit(e.target.value);
                          handleUnitsChange('coilLength', e.target.value);
                        }}
                      >
                        <Radio.Button value="mm">mm</Radio.Button>
                        <Radio.Button value="inch">in</Radio.Button>
                      </Radio.Group>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item name="cableLength" className="pqt-hp-report-field-wrap">
                  <div className="pqt-hp-report-field">
                    <div className="pqt-hp-report-field__head">
                      <Checkbox
                        onChange={(e) => {
                          handleHeaderCheckboxChange('cableLength', e.target.checked);
                        }}
                        checked={isArrayNotEmpty('cableLength')}
                      />
                      <span className="pqt-hp-report-field__label">Cable length</span>
                      {isArrayNotEmpty('cableLength') ? (
                        <span className="pqt-hp-report-field__req" aria-hidden>
                          *
                        </span>
                      ) : null}
                    </div>
                    <div className="pqt-hp-num-unit">
                      <Input
                        disabled={!isArrayNotEmpty('cableLength')}
                        value={formData.cableLength}
                        onChange={(e) => handleInputChange('cableLength', e.target.value)}
                        type="number"
                        inputMode="decimal"
                        placeholder="Length"
                      />
                      <Radio.Group
                        className="pqt-hp-unit-radios"
                        optionType="button"
                        buttonStyle="solid"
                        size="small"
                        disabled={!isArrayNotEmpty('cableLength')}
                        value={getCategoryValue1('cableLength')}
                        onChange={(e) => {
                          setcableUnit(e.target.value);
                          handleUnitsChange('cableLength', e.target.value);
                        }}
                      >
                        <Radio.Button value="meters">m</Radio.Button>
                        <Radio.Button value="feet">ft</Radio.Button>
                      </Radio.Group>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item name="numberOfTurns" className="pqt-hp-report-field-wrap">
                  <div className="pqt-hp-report-field">
                    <div className="pqt-hp-report-field__head">
                      <Checkbox
                        onChange={(e) => {
                          handleHeaderCheckboxChange('numberOfTurns', e.target.checked);
                        }}
                        checked={isArrayNotEmpty('numberOfTurns')}
                      />
                      <span className="pqt-hp-report-field__label">Number of turns</span>
                      {isArrayNotEmpty('numberOfTurns') ? (
                        <span className="pqt-hp-report-field__req" aria-hidden>
                          *
                        </span>
                      ) : null}
                    </div>
                    <Input
                      disabled={!isArrayNotEmpty('numberOfTurns')}
                      value={formData.numberOfTurns}
                      onChange={(e) => handleInputChange('numberOfTurns', e.target.value)}
                      type="number"
                      inputMode="numeric"
                      placeholder="Turns"
                    />
                  </div>
                </Form.Item>
              </div>
            </Col>

            <Col span={24}>
              <hr className="pqt-hp-section-divider" />
            </Col>

            {/* Cut back Length */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Cut back length" name="cutbackLength" required>
                <Input
                  value={formData.cutbackLength}
                  onChange={(e) => handleInputChange('cutbackLength', e.target.value)}
                />
              </Form.Item>
            </Col>

            {/* Parent Coating Thickness */}
            <Col xs={24} md={16} lg={10}>
              <Form.Item label="Parent coating thickness" name="parentCoatingThickness" required>
                <div className="pqt-hp-thickness-row">
                  <Input
                    value={formData.parentCoatingThickness}
                    onChange={(e) => handleInputChange('parentCoatingThickness', e.target.value)}
                    type="number"
                    inputMode="decimal"
                    placeholder="Thickness"
                  />
                  <PqtFormSelect
                    variant="compact"
                    placeholder="Unit"
                    value={getCategoryValue1('parentCoatingThickness')}
                    options={COATING_THICKNESS_UNIT_OPTIONS}
                    onChange={(v) => {
                      setParentCoatingThicknessUnit(v);
                      handleUnitsChange('parentCoatingThickness', v);
                    }}
                  />
                </div>
              </Form.Item>
            </Col>

            {/* Probe Temp */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label={`Probe temp (${temperatureUnit})`} name="probeTemp" required>
                <Input
                  value={formData.probeTemp}
                  onChange={(e) => handleInputChange('probeTemp', e.target.value)}
                  type="number"
                  inputMode="decimal"
                />
              </Form.Item>
            </Col>

            {/* Probe Temp Location */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Probe temp location" name="probeTempLocation" required>
                <Input
                  value={formData.probeTempLocation}
                  onChange={(e) => handleInputChange('probeTempLocation', e.target.value)}
                  maxLength={255}
                />
              </Form.Item>
            </Col>

            {/* Signature + file upload */}
            <Col xs={24} lg={8}>
              <Form.Item label="Signature required" name="signatureRequired" required>
                <Radio.Group
                  className="pqt-hp-signature-radios"
                  optionType="button"
                  buttonStyle="solid"
                  value={formData.signatureRequired}
                  onChange={(e) => handleInputChange('signatureRequired', e.target.value)}
                >
                  <Radio.Button value={0}>No</Radio.Button>
                  <Radio.Button value={1}>Yes</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col xs={24} lg={16}>
              <Form.Item label="Heat profile data file (.txt, .csv)" required className="uploadFile_heatProfile">
                <div className="pqt-hp-file-row">
                  <Upload
                    maxCount={1}
                    showUploadList={false}
                    accept=".txt,.csv"
                    onChange={handleChange}
                    fileList={fileList}
                  >
                    <Button type="default" className="pqt-hp-upload-btn">
                      <UploadIcon size={18} strokeWidth={2} aria-hidden />
                      Choose file
                    </Button>
                  </Upload>
                  {selectedFile ? (
                    <div className="pqt-hp-file-chip">
                      <div className="pqt-hp-file-chip__name" title={selectedFile.name}>
                        <FileText className="pqt-hp-file-chip__icon" size={16} strokeWidth={2} aria-hidden />
                        <span>{displayFileName(selectedFile.name)}</span>
                      </div>
                      <Button
                        type="default"
                        danger
                        className="pqt-hp-file-remove"
                        onClick={() => {
                          setSelectedFile(null);
                          setFileList([]);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: '#94a3b8', alignSelf: 'center' }}>
                      No file selected
                    </span>
                  )}
                </div>
              </Form.Item>
            </Col>

            {/* Generated PDF Display */}
            <Col span={24}>
              {outputFile ? (
                <Form.Item label="Generated PDF">
                  <a
                    className="pqt-hp-pdf-link"
                    href={outputFile}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download size={18} strokeWidth={2} aria-hidden />
                    Download PDF
                  </a>
                </Form.Item>
              ) : null}
            </Col>
          </Row>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ProjectHeatProfile;
